import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { Hotel, Wifi, Gift, Eye, Save, Loader2, Settings2, Star, ExternalLink, MapPin, Info, DollarSign, Calendar, Upload, Image as ImageIcon, Trash2, Globe, ArrowLeft, Smartphone, Users, Bath, LogOut, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { RoomFeedbackConfig } from '../components/hotel/RoomFeedbackConfig';
import { QuestionManager } from '../components/cards/QuestionManager';
import AssistantSetup from './AssistantSetup';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const TOUCHPOINT_PRESETS = {
    welcome: { wifi_enabled: true, rating_enabled: true, gift_enabled: true, public_rating_enabled: true },
    lobby_wifi: { wifi_enabled: true, rating_enabled: false, gift_enabled: false, public_rating_enabled: false },
    checkout: { wifi_enabled: false, rating_enabled: true, gift_enabled: false, public_rating_enabled: true },
    in_stay: { wifi_enabled: false, rating_enabled: true, gift_enabled: false, public_rating_enabled: false },
    room_feedback: { wifi_enabled: false, rating_enabled: false, gift_enabled: false, public_rating_enabled: false },
};

const LANG_FLAGS = { en: 'EN', fr: 'FR', es: 'ES' };

const HotelSetup = () => {
    const { t } = useTranslation();
    const [cards, setCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState('');
    const [config, setConfig] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [giftLang, setGiftLang] = useState('en');
    const [msgLang, setMsgLang] = useState('en');
    const [organizations, setOrganizations] = useState([]);
    const [uploadingHotelLogo, setUploadingHotelLogo] = useState(false);
    const [uploadingVoucherLogo, setUploadingVoucherLogo] = useState(false);
    const [orgWallpaper, setOrgWallpaper] = useState('');
    const [wallpaperScale, setWallpaperScale] = useState(100);
    const [orgVoucherSettings, setOrgVoucherSettings] = useState(null);
    const [savingWallpaper, setSavingWallpaper] = useState(false);
    const [expType, setExpType] = useState(null);
    const hotelLogoRef = useRef(null);
    const voucherLogoRef = useRef(null);

    useEffect(() => { loadCards(); loadOrganizations(); }, []);

    const handleLogoUpload = async (logoType) => {
        const ref = logoType === 'hotel' ? hotelLogoRef : voucherLogoRef;
        const file = ref.current?.files?.[0];
        if (!file) return;
        if (!selectedCard) { toast.error('Select a hotel card first'); return; }
        if (!file.type.startsWith('image/')) { toast.error(t('hotel.selectImageFile', 'Please select an image file')); return; }
        if (file.size > 5 * 1024 * 1024) { toast.error(t('hotel.logoTooLarge', 'Logo must be under 5MB')); return; }
        const setter = logoType === 'hotel' ? setUploadingHotelLogo : setUploadingVoucherLogo;
        setter(true);
        try {
            const fd = new FormData();
            fd.append('logo', file);
            const res = await api.post(`/hotel/admin/upload-logo/${selectedCard}/${logoType}`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (res.data?.logo_url) {
                const fullUrl = `${API}${res.data.logo_url}`;
                if (logoType === 'hotel') {
                    setConfig(prev => ({ ...prev, hotel_logo_url: fullUrl, has_hotel_logo: true }));
                } else {
                    setConfig(prev => ({ ...prev, separate_voucher_logo_url: fullUrl, has_separate_voucher_logo: true }));
                }
                toast.success(logoType === 'hotel'
                    ? t('hotel.hotelLogoUploaded', 'Hotel logo uploaded')
                    : t('hotel.voucherLogoUploaded', 'Voucher logo uploaded')
                );
            }
        } catch (e) {
            toast.error(t('hotel.logoUploadFailed', 'Logo upload failed'));
        } finally {
            setter(false);
            if (ref.current) ref.current.value = '';
        }
    };

    const handleDeleteLogo = async (logoType) => {
        if (!selectedCard) return;
        try {
            await api.delete(`/hotel/admin/delete-logo/${selectedCard}/${logoType}`);
            if (logoType === 'hotel') {
                setConfig(prev => ({ ...prev, hotel_logo_url: null, has_hotel_logo: false }));
            } else {
                setConfig(prev => ({ ...prev, separate_voucher_logo_url: null, has_separate_voucher_logo: false }));
            }
            toast.success('Logo removed');
        } catch (e) { toast.error('Failed to remove logo'); }
    };

    const loadOrganizations = async () => {
        try {
            const res = await api.get('/hotel/admin/organizations');
            setOrganizations(res.data || []);
        } catch (e) { /* ignored */ }
    };

    const loadCards = async () => {
        try {
            const res = await api.get('/cards');
            const hotelCards = res.data.filter(c => c.interaction_mode === 'hotel');
            setCards(hotelCards);
            if (hotelCards.length > 0) {
                setSelectedCard(hotelCards[0].card_id);
                await loadConfig(hotelCards[0].card_id);
                loadOrgWallpaper(hotelCards[0].card_id, hotelCards);
            }
        } catch (e) { /* ignored */ } finally { setLoading(false); }
    };

    const loadConfig = async (cardId) => {
        try {
            const res = await api.get(`/hotel/admin/config/${cardId}`);
            const cfg = res.data || {};
            // Make logo URLs absolute
            if (cfg.hotel_logo_url && cfg.hotel_logo_url.startsWith('/api')) {
                cfg.hotel_logo_url = `${API}${cfg.hotel_logo_url}`;
            }
            if (cfg.separate_voucher_logo_url && cfg.separate_voucher_logo_url.startsWith('/api')) {
                cfg.separate_voucher_logo_url = `${API}${cfg.separate_voucher_logo_url}`;
            }
            setConfig(cfg);
        } catch (e) { setConfig({ card_id: cardId }); }
    };

    const wallpaperFileRef = useRef(null);
    const handleWallpaperUpload = async (file) => {
        if (!file) return;
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post('/upload/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setOrgWallpaper(r.data.url);
            toast.success(t('hotel.wallpaperUploaded', 'Image uploaded — click Save Wallpaper to apply'));
        } catch (e) { toast.error(t('assistant.brandUploadFailed', 'Upload failed — use an image under 5MB')); }
        finally { if (wallpaperFileRef.current) wallpaperFileRef.current.value = ''; }
    };

    const handleRemoveHotelLogo = async () => {
        if (config.has_uploaded_hotel_logo) { handleDeleteLogo('hotel'); return; }
        try {
            await api.put(`/hotel/admin/config/${selectedCard}`, { ...config, hotel_logo_external_url: '' });
            setConfig(prev => ({ ...prev, hotel_logo_external_url: '', hotel_logo_url: null, has_hotel_logo: false }));
            toast.success(t('hotel.logoRemoved', 'Logo removed'));
        } catch (e) { toast.error(t('hotel.logoRemoveFailed', 'Could not remove logo')); }
    };

    const handleCardChange = async (cardId) => {
        setSelectedCard(cardId);
        await loadConfig(cardId);
        loadOrgWallpaper(cardId);
    };

    // Shared org wallpaper — reuses the EXISTING voucher-settings brand cascade
    // (voucher_settings.background_image_url) consumed by Review Shield,
    // Surveys and the Guest Assistant. No new branding system.
    const loadOrgWallpaper = async (cardId, list) => {
        const card = (list || cards).find(c => c.card_id === cardId);
        if (!card?.organization_id) { setOrgVoucherSettings(null); setOrgWallpaper(''); return; }
        try {
            const res = await api.get(`/voucher-settings?organization_id=${card.organization_id}`);
            setOrgVoucherSettings(res.data || {});
            setOrgWallpaper(res.data?.background_image_url || '');
            setWallpaperScale(res.data?.background_image_scale || 100);
        } catch (e) { setOrgVoucherSettings(null); setOrgWallpaper(''); setWallpaperScale(100); }
    };

    const handleSaveWallpaper = async (urlOverride) => {
        const card = cards.find(c => c.card_id === selectedCard);
        if (!card?.organization_id) return;
        const url = (urlOverride !== undefined ? urlOverride : orgWallpaper).trim();
        setSavingWallpaper(true);
        try {
            const s = orgVoucherSettings || {};
            await api.put(`/voucher-settings?organization_id=${card.organization_id}`, {
                preset_amounts: s.preset_amounts || [20, 25, 50, 75],
                preset_expiry_days: s.preset_expiry_days || [30, 60, 90],
                default_expiry_days: s.default_expiry_days || 30,
                default_message: s.default_message ?? null,
                logo_size: s.logo_size || 40,
                card_style: s.card_style ?? null,
                background_image_url: url,
                background_image_scale: wallpaperScale,
            });
            toast.success(url
                ? t('hotel.wallpaperSaved', 'Hotel wallpaper saved')
                : t('hotel.wallpaperRemoved', 'Hotel wallpaper removed'));
            if (urlOverride !== undefined) setOrgWallpaper(urlOverride);
            loadOrgWallpaper(selectedCard);
        } catch (e) {
            toast.error(t('hotel.wallpaperSaveFailed', 'Could not save wallpaper'));
        } finally { setSavingWallpaper(false); }
    };

    const handleTouchpointChange = (type) => {
        const preset = TOUCHPOINT_PRESETS[type] || TOUCHPOINT_PRESETS.welcome;
        setConfig(prev => ({ ...prev, touchpoint_type: type, ...preset }));
    };

    const updateCapability = (key, value) => {
        setConfig(prev => {
            const next = { ...prev, [key]: value };
            if (key === 'rating_enabled' && !value) {
                next.gift_enabled = false;
                next.public_rating_enabled = false;
            }
            if ((key === 'gift_enabled' || key === 'public_rating_enabled') && value) {
                next.rating_enabled = true;
            }
            return next;
        });
    };

    const handleSave = async () => {
        if (!selectedCard) return;
        setSaving(true);
        try {
            await api.put(`/hotel/admin/config/${selectedCard}`, config);
            toast.success(t('hotel.configSaved', 'Hotel configuration saved'));
        } catch (e) {
            toast.error(t('hotel.configSaveFailed', 'Failed to save configuration'));
        } finally { setSaving(false); }
    };

    const updateConfig = (key, value) => {
        setConfig(prev => ({ ...prev, [key]: value }));
    };

    const tp = config.touchpoint_type || 'welcome';
    const wifiOn = config.wifi_enabled !== false;
    const ratingOn = config.rating_enabled !== false;
    const giftOn = config.gift_enabled !== false;
    const publicRatingOn = config.public_rating_enabled !== false;

    const LangTabs = ({ value, onChange, testIdPrefix }) => (
        <div className="flex gap-1 mb-3" data-testid={`${testIdPrefix}-lang-tabs`}>
            {Object.entries(LANG_FLAGS).map(([code, label]) => (
                <button key={code} type="button" onClick={() => onChange(code)}
                    className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                        value === code
                            ? 'bg-amber-500 text-white'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                    data-testid={`${testIdPrefix}-lang-${code}`}>
                    {label}
                </button>
            ))}
        </div>
    );

    if (loading) {
        return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;
    }

    // ── Experience-Type-First workflow (UX correction) ──
    const EXP_TYPES = [
        { id: 'welcome', icon: <Hotel className="w-6 h-6 text-amber-500" />, label: t('hotel.expWelcome', 'Welcome'), desc: t('hotel.expWelcomeDesc', 'First tap in the room — Wi-Fi, greeting, arrival feedback and gift.') },
        { id: 'lobby_wifi', icon: <Wifi className="w-6 h-6 text-sky-500" />, label: t('hotel.expWifi', 'Wi-Fi'), desc: t('hotel.expWifiDesc', 'Instant Wi-Fi access cards for rooms or the lobby.') },
        { id: 'in_stay', icon: <Bath className="w-6 h-6 text-teal-500" />, label: t('hotel.expInStay', 'Bathroom / In-Stay'), desc: t('hotel.expInStayDesc', 'Quick feedback during the stay (bathroom, room condition).') },
        { id: 'checkout', icon: <LogOut className="w-6 h-6 text-violet-500" />, label: t('hotel.expCheckout', 'Checkout'), desc: t('hotel.expCheckoutDesc', 'Departure feedback and public review invitation.') },
        { id: 'room_feedback', icon: <MessageSquare className="w-6 h-6 text-orange-500" />, label: t('hotel.expRoomFeedback', 'Room Feedback'), desc: t('hotel.expRoomFeedbackDesc', 'Dedicated in-room feedback with photo capture.') },
        { id: 'assistant', icon: <Smartphone className="w-6 h-6 text-emerald-500" />, label: t('hotel.expAssistant', 'Guest Experience / In-Room Assistant'), desc: t('hotel.expAssistantDesc', 'The full in-room menu: security, hotel info, food, housekeeping, concierge, messages, sponsor.') },
        { id: 'staff_hub', icon: <Users className="w-6 h-6 text-rose-500" />, label: t('hotel.expStaffHub', 'Hotel Ops'), desc: t('hotel.expStaffHubDesc', 'Operational access for housekeeping, security, concierge and managers.') },
    ];

    const headerLite = (
        <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Hotel className="w-6 h-6 text-amber-500" />
                {t('hotel.setupTitle', 'Hotel Setup')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{t('hotel.expSubtitle', 'A hotel can run several experiences at once — each one is set up and saved independently.')}</p>
        </div>
    );
    const changeTypeLink = (
        <button type="button" onClick={() => setExpType(null)}
            className="inline-flex items-center gap-1.5 text-sm text-amber-600 hover:underline"
            data-testid="exp-change-type-btn">
            <ArrowLeft className="w-4 h-4" /> {t('hotel.expChange', 'Choose a different experience type')}
        </button>
    );

    if (!expType) {
        return (
            <div className="space-y-6" data-testid="hotel-setup">
                {headerLite}
                <Card data-testid="experience-picker">
                    <CardHeader>
                        <CardTitle className="text-base">{t('hotel.expTitle', 'What would you like to set up?')}</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {EXP_TYPES.map((e) => (
                            <button key={e.id} type="button" onClick={() => setExpType(e.id)}
                                className="flex items-start gap-3 border rounded-xl p-4 text-left hover:border-amber-400 hover:bg-amber-50/40 dark:hover:bg-amber-500/5 transition-colors"
                                data-testid={`exp-type-${e.id}`}>
                                <div className="shrink-0 mt-0.5">{e.icon}</div>
                                <div>
                                    <p className="text-sm font-semibold">{e.label}</p>
                                    <p className="text-xs text-muted-foreground mt-1">{e.desc}</p>
                                </div>
                            </button>
                        ))}
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (expType === 'assistant') {
        return (
            <div className="space-y-6" data-testid="hotel-setup">
                {headerLite}
                {changeTypeLink}
                <p className="text-xs text-muted-foreground">
                    {t('hotel.expBrandingHint', 'Hotel logo and wallpaper are shared branding — configure them once under any touchpoint experience; the Assistant inherits them automatically.')}
                </p>
                <AssistantSetup />
            </div>
        );
    }

    if (expType === 'staff_hub') {
        return (
            <div className="space-y-6" data-testid="hotel-setup">
                {headerLite}
                {changeTypeLink}
                <Card data-testid="staff-hub-setup-card">
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            <Users className="w-4 h-4 text-rose-500" /> {t('hotel.expStaffHub', 'Hotel Ops')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm">
                        <div>
                            <p className="font-semibold">{t('hotel.staffHubStep1', '1. Give your team access')}</p>
                            <p className="text-muted-foreground text-xs mt-1">{t('hotel.staffHubStep1Desc', 'Assign each employee their hotel functions (Housekeeping, Security, Concierge, Management) on the Employees page. Staff identify themselves with their existing 4-digit PIN.')}</p>
                            <Button variant="outline" size="sm" className="mt-2" onClick={() => window.location.assign('/employees')} data-testid="staffhub-employees-btn">
                                {t('hotel.staffHubEmployees', 'Manage employees')}
                            </Button>
                        </div>
                        <div>
                            <p className="font-semibold">{t('hotel.staffHubStep2', '2. Place staff cards')}</p>
                            <p className="text-muted-foreground text-xs mt-1">{t('hotel.staffHubStep2Desc', 'Create cards in the card fleet and set their mode to "Hotel — Ops". Mount shared cards in hallways or give employees individual cards — both work the same way.')}</p>
                        </div>
                        <div>
                            <p className="font-semibold">{t('hotel.staffHubStep3', '3. Choose active functions')}</p>
                            <p className="text-muted-foreground text-xs mt-1">{t('hotel.staffHubStep3Desc', 'Turn whole functions on or off for the entire hotel under Guest Experience → Staff functions.')}</p>
                            <Button variant="outline" size="sm" className="mt-2" onClick={() => setExpType('assistant')} data-testid="staffhub-assistant-btn">
                                {t('hotel.staffHubOpenAssistant', 'Open Guest Experience settings')}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (cards.length === 0) {
        return (
            <div className="space-y-6" data-testid="hotel-setup">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Hotel className="w-6 h-6 text-amber-500" />
                        {t('hotel.setupTitle', 'Hotel Setup')}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {t('hotel.noHotelCards', 'No cards are set to Hotel mode yet. Go to Cards \u2192 Card Settings \u2192 Set Interaction Mode to "Hotel" to get started.')}
                    </p>
                    <div className="mt-3">{changeTypeLink}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="hotel-setup">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Hotel className="w-6 h-6 text-amber-500" />
                        {t('hotel.setupTitle', 'Hotel Setup')}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {t('hotel.setupSubtitle', 'Configure the Hotel guest experience')}
                        {' '}<a href="/card-guide#quick-start" className="text-xs text-amber-600 underline" data-testid="setup-card-guide-link">{t('cardGuide.setupHelp', 'How do I create and program a hotel card?')}</a>
                        {' · '}<a href="/card-guide#wifi" className="text-xs text-amber-600 underline" data-testid="setup-wifi-guide-link">{t('cardGuide.wifiHelp', 'How do I create a Wi-Fi Tap card?')}</a>
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowPreview(!showPreview)} data-testid="hotel-preview-btn">
                        <Eye className="w-4 h-4 mr-2" />{t('hotel.preview', 'Preview')}
                    </Button>
                    <Button onClick={handleSave} disabled={saving} data-testid="hotel-save-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        {t('common.save', 'Save')}
                    </Button>
                </div>
            </div>

            {changeTypeLink}

            {/* Card selector */}
            {cards.length > 1 && (
                <Select value={selectedCard} onValueChange={handleCardChange}>
                    <SelectTrigger className="w-[280px]" data-testid="hotel-card-selector">
                        <SelectValue placeholder="Select hotel card" />
                    </SelectTrigger>
                    <SelectContent>
                        {cards.map(c => (
                            <SelectItem key={c.card_id} value={c.card_id}>
                                {c.card_id} — {c.name || 'Hotel Card'}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}

            {/* Preview */}
            {showPreview && (
                <Card>
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between gap-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <Eye className="w-4 h-4" />{t('hotel.livePreview', 'Live Preview')}
                            </CardTitle>
                            <Button variant="outline" size="sm" onClick={() => setShowPreview(false)} data-testid="hotel-preview-back-btn">
                                <ArrowLeft className="w-4 h-4 mr-1.5" />{t('hotel.backToSetup', 'Back to Hotel Setup')}
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="rounded-xl overflow-hidden border" style={{ maxWidth: 400, margin: '0 auto' }}>
                            <iframe
                                src={`${API}/hotel/${selectedCard}?preview=1`}
                                className="w-full h-[600px] border-0"
                                title="Hotel Preview"
                                data-testid="hotel-preview-iframe"
                            />
                        </div>
                        <div className="flex justify-center mt-3">
                            <Button variant="outline" size="sm" onClick={() => setShowPreview(false)} data-testid="hotel-preview-back-btn-bottom">
                                <ArrowLeft className="w-4 h-4 mr-1.5" />{t('hotel.backToSetup', 'Back to Hotel Setup')}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Touchpoint Configuration */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-amber-500" />
                        {t('hotel.touchpointConfig', 'Touchpoint Configuration')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label>{t('hotel.touchpointType', 'Touchpoint Type')}</Label>
                            <Select value={tp} onValueChange={handleTouchpointChange}>
                                <SelectTrigger className="mt-1" data-testid="hotel-touchpoint-type">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="room_feedback">{t('hotel.touchpointRoomFeedback', 'Welcome / Room')}</SelectItem>
                                    <SelectItem value="welcome">{t('hotel.touchpointWelcome', 'Wi-Fi / Room')}</SelectItem>
                                    <SelectItem value="lobby_wifi">{t('hotel.touchpointLobbyWifi', 'Wi-Fi / Lobby')}</SelectItem>
                                    <SelectItem value="checkout">{t('hotel.touchpointCheckout', 'Checkout')}</SelectItem>
                                    <SelectItem value="in_stay">{t('hotel.touchpointInStay', 'Bathroom / In-Stay')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>{t('hotel.touchpointName', 'Touchpoint Name')}</Label>
                            <Input
                                value={config.touchpoint_name || ''}
                                onChange={e => updateConfig('touchpoint_name', e.target.value)}
                                placeholder={t('hotel.touchpointNamePlaceholder', 'Optional custom name...')}
                                className="mt-1"
                                data-testid="hotel-touchpoint-name"
                            />
                        </div>
                    </div>

                    {/* Capabilities — not applicable to the pure Room Feedback experience */}
                    {tp !== 'room_feedback' && (
                    <div>
                        <Label className="text-sm font-medium mb-3 block">{t('hotel.capabilities', 'Capabilities')}</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div className="flex items-center justify-between p-3 rounded-lg border bg-card" data-testid="hotel-cap-wifi">
                                <div className="flex items-center gap-2">
                                    <Wifi className="w-4 h-4 text-blue-500" />
                                    <span className="text-sm">Wi-Fi</span>
                                </div>
                                <Switch checked={wifiOn} onCheckedChange={v => updateCapability('wifi_enabled', v)} data-testid="hotel-wifi-toggle" />
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-lg border bg-card" data-testid="hotel-cap-rating">
                                <div className="flex items-center gap-2">
                                    <Star className="w-4 h-4 text-amber-500" />
                                    <span className="text-sm">{t('hotel.ratingCap', 'Rating')}</span>
                                </div>
                                <Switch checked={ratingOn} onCheckedChange={v => updateCapability('rating_enabled', v)} data-testid="hotel-rating-toggle" />
                            </div>
                            <div className={`flex items-center justify-between p-3 rounded-lg border bg-card ${!ratingOn ? 'opacity-50' : ''}`} data-testid="hotel-cap-gift">
                                <div className="flex items-center gap-2">
                                    <Gift className="w-4 h-4 text-amber-500" />
                                    <span className="text-sm">{t('hotel.giftCap', 'Gift')}</span>
                                </div>
                                <Switch checked={giftOn} disabled={!ratingOn} onCheckedChange={v => updateCapability('gift_enabled', v)} data-testid="hotel-gift-toggle" />
                            </div>
                            <div className={`flex items-center justify-between p-3 rounded-lg border bg-card ${!ratingOn ? 'opacity-50' : ''}`} data-testid="hotel-cap-public-rating">
                                <div className="flex items-center gap-2">
                                    <ExternalLink className="w-4 h-4 text-blue-500" />
                                    <span className="text-sm">{t('hotel.publicRatingCap', 'Public Rating')}</span>
                                </div>
                                <Switch checked={publicRatingOn} disabled={!ratingOn} onCheckedChange={v => updateCapability('public_rating_enabled', v)} data-testid="hotel-public-rating-toggle" />
                            </div>
                        </div>
                        {!ratingOn && tp !== 'room_feedback' && (
                            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                                <Info className="w-3 h-3" />
                                {t('hotel.requiresRating', 'Gift and Public Rating require Rating to be enabled')}
                            </p>
                        )}
                    </div>
                    )}

                    {/* Language selection gate toggle */}
                    <div className="flex items-center justify-between p-3 rounded-lg border bg-card" data-testid="hotel-cap-language-selection">
                        <div className="flex items-start gap-2">
                            <Globe className="w-4 h-4 text-emerald-500 mt-0.5" />
                            <div>
                                <span className="text-sm">{t('hotel.showLanguageSelection', 'Show Language Selection')}</span>
                                <p className="text-xs text-muted-foreground mt-0.5">{t('hotel.showLanguageSelectionHint', 'When enabled, guests choose EN / FR / ES on a first screen, before the welcome page.')}</p>
                            </div>
                        </div>
                        <Switch checked={config.language_selection_enabled === true} onCheckedChange={v => updateConfig('language_selection_enabled', v)} data-testid="hotel-language-selection-toggle" />
                    </div>
                </CardContent>
            </Card>

            {/* Hotel Logo — Independent of Gift/Voucher */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-amber-500" />
                        {t('hotel.hotelLogo', 'Hotel Logo')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                        {t('hotel.hotelLogoHint', 'This logo appears in the guest flow (welcome, rating screens). Upload once — it persists with the hotel configuration.')}
                    </p>
                    <div className="flex items-start gap-4">
                        <div
                            className="rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center bg-muted/30 overflow-hidden flex-shrink-0 transition-all duration-300"
                            style={{ width: `${Math.max(60, (config.hotel_logo_size || 40) * 1.2)}px`, height: `${Math.max(50, config.hotel_logo_size || 40)}px` }}
                            data-testid="hotel-logo-preview"
                        >
                            {config.has_hotel_logo && config.hotel_logo_url ? (
                                <img src={config.hotel_logo_url} alt="Hotel Logo" className="max-w-full max-h-full object-contain p-1" />
                            ) : (
                                <Hotel className="w-6 h-6 text-muted-foreground/40" />
                            )}
                        </div>
                        <div className="space-y-2 flex-1">
                            <input ref={hotelLogoRef} type="file" accept="image/*" onChange={() => handleLogoUpload('hotel')} className="hidden" />
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => hotelLogoRef.current?.click()} disabled={uploadingHotelLogo} data-testid="hotel-upload-hotel-logo-btn">
                                    {uploadingHotelLogo ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                                    {config.has_hotel_logo ? t('hotel.replaceLogo', 'Replace Logo') : t('hotel.uploadLogo', 'Upload Logo')}
                                </Button>
                                {config.has_hotel_logo && (
                                    <Button variant="ghost" size="sm" onClick={handleRemoveHotelLogo} className="text-destructive hover:text-destructive" data-testid="hotel-delete-hotel-logo-btn">
                                        <Trash2 className="w-3 h-3 mr-1" />{t('hotel.removeLogo', 'Remove')}
                                    </Button>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground">{t('hotel.logoFileTypes', 'PNG, JPG, SVG, WebP — max 5MB')}</p>
                            {/* Logo URL — existing URL-based structure (externally hosted images stay supported) */}
                            {!config.has_uploaded_hotel_logo && (
                                <div className="space-y-1" data-testid="hotel-logo-url-block">
                                    <Label className="text-xs text-muted-foreground">{t('hotel.logoUrlLabel', 'Or paste an image URL')}</Label>
                                    <Input
                                        value={config.hotel_logo_external_url || ''}
                                        onChange={e => setConfig(prev => ({
                                            ...prev, hotel_logo_external_url: e.target.value,
                                            hotel_logo_url: e.target.value.trim() || null,
                                            has_hotel_logo: !!e.target.value.trim(),
                                        }))}
                                        placeholder={t('hotel.logoUrlPlaceholder', 'https://… logo image URL')}
                                        className="text-xs" data-testid="hotel-logo-url-input" />
                                    <p className="text-[11px] text-muted-foreground">{t('hotel.logoUrlHint', 'The preview updates as you type. Use the Save button below to keep it.')}</p>
                                </div>
                            )}
                            {/* Hotel Logo Size — same 20-120px range as production voucher system */}
                            {config.has_hotel_logo && (
                                <div className="pt-1 space-y-1" data-testid="hotel-logo-size-control">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-xs text-muted-foreground">{t('hotel.logoSize', 'Logo Size')}</Label>
                                        <span className="text-xs text-muted-foreground">{config.hotel_logo_size || 40}px</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button type="button"
                                            onClick={() => updateConfig('hotel_logo_size', Math.max(20, (config.hotel_logo_size || 40) - 5))}
                                            className="w-7 h-7 rounded-md border text-sm font-bold hover:bg-muted shrink-0"
                                            aria-label={t('hotel.smaller', 'Smaller')} data-testid="hotel-logo-size-minus">−</button>
                                        <input type="range" min={20} max={120}
                                            value={config.hotel_logo_size || 40}
                                            onChange={e => updateConfig('hotel_logo_size', parseInt(e.target.value))}
                                            className="w-full accent-amber-500" data-testid="hotel-logo-size-slider" />
                                        <button type="button"
                                            onClick={() => updateConfig('hotel_logo_size', Math.min(120, (config.hotel_logo_size || 40) + 5))}
                                            className="w-7 h-7 rounded-md border text-sm font-bold hover:bg-muted shrink-0"
                                            aria-label={t('hotel.larger', 'Larger')} data-testid="hotel-logo-size-plus">+</button>
                                    </div>
                                    <div className="flex justify-between text-[10px] text-muted-foreground">
                                        <span>{t('hotel.small', 'Small')}</span>
                                        <span>{t('hotel.large', 'Large')}</span>
                                    </div>
                                </div>
                            )}
                            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="hotel-logo-save-btn">
                                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                                {t('hotel.logoSave', 'Save Logo Settings')}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Shared brand wallpaper — existing YulOnTap branding cascade */}
            <Card className="border-l-4 border-l-sky-500" data-testid="hotel-wallpaper-card">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-sky-500" />
                        {t('hotel.wallpaperTitle', 'Hotel Wallpaper (Shared Brand)')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                        {t('hotel.wallpaperHint', "One shared wallpaper for this hotel's organization — inherited by the Guest Assistance Platform, Review Shield and Surveys through the existing branding cascade.")}
                    </p>
                    {orgWallpaper && (
                        <div className="w-full h-32 rounded-lg border overflow-hidden relative" data-testid="hotel-wallpaper-preview">
                            <div className="absolute inset-0" style={{
                                backgroundImage: `url("${orgWallpaper}")`,
                                backgroundSize: 'cover', backgroundPosition: 'center',
                                transform: `scale(${(wallpaperScale || 100) / 100})`, transformOrigin: 'center',
                            }} />
                        </div>
                    )}
                    <input ref={wallpaperFileRef} type="file" accept="image/*" className="hidden"
                        onChange={(e) => handleWallpaperUpload(e.target.files?.[0])} />
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => wallpaperFileRef.current?.click()}
                            data-testid="hotel-wallpaper-upload-btn">
                            <Upload className="w-3 h-3 mr-1" />{t('assistant.brandUpload', 'Upload image')}
                        </Button>
                        <Input
                            value={orgWallpaper}
                            onChange={(e) => setOrgWallpaper(e.target.value)}
                            placeholder={t('hotel.wallpaperPlaceholder', 'https://… image URL')}
                            data-testid="hotel-wallpaper-input"
                        />
                        {orgWallpaper && (
                            <Button variant="ghost" size="sm" onClick={() => handleSaveWallpaper('')} disabled={savingWallpaper}
                                className="text-destructive hover:text-destructive shrink-0" data-testid="hotel-wallpaper-remove-btn">
                                <Trash2 className="w-3 h-3 mr-1" />{t('hotel.wallpaperRemove', 'Remove')}
                            </Button>
                        )}
                    </div>
                    {orgWallpaper && (
                        <div className="space-y-1" data-testid="hotel-wallpaper-scale-control">
                            <div className="flex items-center justify-between">
                                <Label className="text-xs text-muted-foreground">{t('hotel.wallpaperScale', 'Wallpaper Zoom')}</Label>
                                <span className="text-xs text-muted-foreground">{wallpaperScale || 100}%</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button type="button"
                                    onClick={() => setWallpaperScale(s => Math.max(100, (s || 100) - 10))}
                                    className="w-7 h-7 rounded-md border text-sm font-bold hover:bg-muted shrink-0"
                                    aria-label={t('hotel.smaller', 'Smaller')} data-testid="hotel-wallpaper-scale-minus">−</button>
                                <input type="range" min={100} max={160} step={10}
                                    value={wallpaperScale || 100}
                                    onChange={e => setWallpaperScale(parseInt(e.target.value))}
                                    className="w-full accent-sky-500" data-testid="hotel-wallpaper-scale-slider" />
                                <button type="button"
                                    onClick={() => setWallpaperScale(s => Math.min(160, (s || 100) + 10))}
                                    className="w-7 h-7 rounded-md border text-sm font-bold hover:bg-muted shrink-0"
                                    aria-label={t('hotel.larger', 'Larger')} data-testid="hotel-wallpaper-scale-plus">+</button>
                            </div>
                            <p className="text-[11px] text-muted-foreground">{t('hotel.wallpaperScaleHint', '100% shows the image normally. Zoom in if the edges of your image should stay out of view.')}</p>
                        </div>
                    )}
                    <Button size="sm" onClick={() => handleSaveWallpaper()} disabled={savingWallpaper} data-testid="hotel-wallpaper-save-btn">
                        {savingWallpaper ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                        {t('hotel.wallpaperSave', 'Save Wallpaper')}
                    </Button>
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Room Feedback experience configuration */}
                {tp === 'room_feedback' && (
                    <div className="lg:col-span-2">
                        <RoomFeedbackConfig
                            config={config}
                            updateConfig={updateConfig}
                            card={cards.find(c => c.card_id === selectedCard)}
                            cards={cards}
                            onSelectCard={handleCardChange}
                        />
                    </div>
                )}

                {/* WiFi Configuration */}
                {wifiOn && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                                <Wifi className="w-4 h-4 text-blue-500" />
                                {t('hotel.wifiConfig', 'Wi-Fi Configuration')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>{t('hotel.wifiSsid', 'Network Name (SSID)')}</Label>
                                    <Input
                                        value={config.wifi_ssid || ''}
                                        onChange={e => updateConfig('wifi_ssid', e.target.value)}
                                        placeholder="HotelName_Guest"
                                        className="mt-1"
                                        data-testid="hotel-wifi-ssid"
                                    />
                                </div>
                                <div>
                                    <Label>{t('hotel.wifiPassword', 'Wi-Fi Password')}</Label>
                                    <Input
                                        value={config.wifi_password || ''}
                                        onChange={e => updateConfig('wifi_password', e.target.value)}
                                        placeholder="Welcome2026"
                                        className="mt-1"
                                        data-testid="hotel-wifi-password"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label>{t('hotel.wifiDelay', 'Wi-Fi Email Delay (seconds)')}</Label>
                                <Input
                                    type="number"
                                    value={config.wifi_email_delay_seconds || 5}
                                    onChange={e => updateConfig('wifi_email_delay_seconds', parseInt(e.target.value) || 5)}
                                    min={1} max={30}
                                    className="mt-1 w-24"
                                    data-testid="hotel-wifi-delay"
                                />
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Gift / Voucher Configuration */}
                {giftOn && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                                <Gift className="w-4 h-4 text-amber-500" />
                                {t('hotel.giftConfig', 'Gift Configuration')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Issuing Business selector */}
                            <div>
                                <Label className="flex items-center gap-1.5 mb-1">
                                    <Hotel className="w-3.5 h-3.5 text-blue-500" />
                                    {t('hotel.issuingBusiness', 'Issuing Business')}
                                </Label>
                                <Select
                                    value={config.gift_issuing_org_id || ''}
                                    onValueChange={v => updateConfig('gift_issuing_org_id', v)}
                                >
                                    <SelectTrigger data-testid="hotel-issuing-org">
                                        <SelectValue placeholder={t('hotel.selectIssuingBusiness', 'Select business for voucher branding...')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {organizations.map(org => (
                                            <SelectItem key={org.id} value={org.id}>
                                                {org.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {t('hotel.issuingBusinessHint', 'The voucher will use this business\u2019s branding and logo. Leave blank to use the hotel\u2019s own organization.')}
                                </p>
                            </div>

                            {/* Voucher Logo Mode */}
                            <div className="border rounded-lg p-4 bg-muted/20 space-y-3" data-testid="hotel-voucher-logo-section">
                                <Label className="flex items-center gap-1.5 text-sm font-medium">
                                    <ImageIcon className="w-3.5 h-3.5 text-amber-500" />
                                    {t('hotel.voucherLogo', 'Voucher Logo')}
                                </Label>
                                <div className="space-y-2">
                                    <label className="flex items-center gap-3 p-2 rounded-md border cursor-pointer hover:bg-muted/30 transition-colors" data-testid="hotel-voucher-mode-hotel">
                                        <input
                                            type="radio"
                                            name="voucher_logo_mode"
                                            checked={(config.voucher_logo_mode || 'hotel_logo') === 'hotel_logo'}
                                            onChange={() => updateConfig('voucher_logo_mode', 'hotel_logo')}
                                            className="accent-amber-500"
                                        />
                                        <div>
                                            <span className="text-sm font-medium">{t('hotel.useHotelLogo', 'Use Hotel Logo')}</span>
                                            <p className="text-xs text-muted-foreground">{t('hotel.useHotelLogoHint', 'The voucher will use the same logo as the hotel guest experience.')}</p>
                                        </div>
                                        {config.has_hotel_logo && config.hotel_logo_url && (
                                            <img src={config.hotel_logo_url} alt="" style={{ height: `${Math.min(config.voucher_logo_size || 40, 60)}px` }} className="w-auto object-contain rounded ml-auto flex-shrink-0 transition-all duration-300" />
                                        )}
                                    </label>
                                    <label className="flex items-center gap-3 p-2 rounded-md border cursor-pointer hover:bg-muted/30 transition-colors" data-testid="hotel-voucher-mode-separate">
                                        <input
                                            type="radio"
                                            name="voucher_logo_mode"
                                            checked={config.voucher_logo_mode === 'separate_logo'}
                                            onChange={() => updateConfig('voucher_logo_mode', 'separate_logo')}
                                            className="accent-amber-500"
                                        />
                                        <div>
                                            <span className="text-sm font-medium">{t('hotel.useDifferentLogo', 'Use a Different Logo')}</span>
                                            <p className="text-xs text-muted-foreground">{t('hotel.useDifferentLogoHint', 'Upload a separate logo for the voucher (e.g., a different business).')}</p>
                                        </div>
                                    </label>
                                </div>
                                {/* Separate Voucher Logo Upload — visible only when mode is separate_logo */}
                                {config.voucher_logo_mode === 'separate_logo' && (
                                    <div className="flex items-start gap-4 pt-2 border-t">
                                        <div
                                            className="rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center bg-muted/30 overflow-hidden flex-shrink-0 transition-all duration-300"
                                            style={{ width: `${Math.max(50, (config.voucher_logo_size || 110) * 0.6)}px`, height: `${Math.max(40, (config.voucher_logo_size || 110) * 0.5)}px` }}
                                            data-testid="hotel-voucher-logo-preview"
                                        >
                                            {config.has_separate_voucher_logo && config.separate_voucher_logo_url ? (
                                                <img src={config.separate_voucher_logo_url} alt="Voucher Logo" className="max-w-full max-h-full object-contain p-1" />
                                            ) : (
                                                <Upload className="w-4 h-4 text-muted-foreground/40" />
                                            )}
                                        </div>
                                        <div className="space-y-2 flex-1">
                                            <input ref={voucherLogoRef} type="file" accept="image/*" onChange={() => handleLogoUpload('voucher')} className="hidden" />
                                            <div className="flex gap-2">
                                                <Button variant="outline" size="sm" onClick={() => voucherLogoRef.current?.click()} disabled={uploadingVoucherLogo} data-testid="hotel-upload-voucher-logo-btn">
                                                    {uploadingVoucherLogo ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                                                    {config.has_separate_voucher_logo ? t('hotel.replaceLogo', 'Replace Logo') : t('hotel.uploadVoucherLogo', 'Upload Voucher Logo')}
                                                </Button>
                                                {config.has_separate_voucher_logo && (
                                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteLogo('voucher')} className="text-destructive hover:text-destructive" data-testid="hotel-delete-voucher-logo-btn">
                                                        <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground">{t('hotel.logoFileTypes', 'PNG, JPG, SVG, WebP — max 5MB')}</p>
                                        </div>
                                    </div>
                                )}
                                {/* Voucher Logo Size — same 20-120px range as production voucher system */}
                                {(config.has_hotel_logo || config.has_separate_voucher_logo) && (
                                    <div className="pt-2 space-y-1 border-t" data-testid="hotel-voucher-logo-size-control">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-xs text-muted-foreground">{t('hotel.voucherLogoSize', 'Voucher Logo Size')}</Label>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">{config.voucher_logo_size || 110}px</span>
                                                <img
                                                    src={config.voucher_logo_mode === 'separate_logo' && config.separate_voucher_logo_url
                                                        ? config.separate_voucher_logo_url
                                                        : config.hotel_logo_url}
                                                    alt=""
                                                    style={{ height: `${Math.min(config.voucher_logo_size || 110, 60)}px` }}
                                                    className="w-auto object-contain rounded transition-all duration-300"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            </div>
                                        </div>
                                        <input type="range" min={20} max={120}
                                            value={config.voucher_logo_size || 110}
                                            onChange={e => updateConfig('voucher_logo_size', parseInt(e.target.value))}
                                            className="w-full accent-amber-500" data-testid="hotel-voucher-logo-size-slider" />
                                        <div className="flex justify-between text-[10px] text-muted-foreground">
                                            <span>{t('hotel.small', 'Small')}</span>
                                            <span>{t('hotel.large', 'Large')}</span>
                                        </div>
                                    </div>
                                )}
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Info className="w-3 h-3 flex-shrink-0" />
                                    {config.voucher_logo_mode === 'separate_logo'
                                        ? t('hotel.separateLogoInfo', 'The voucher will display the separate logo. Changing the hotel logo will not affect the voucher.')
                                        : t('hotel.hotelLogoInfo', 'The voucher will display the hotel logo. No separate upload needed.')
                                    }
                                </p>
                            </div>

                            {/* Amount + Expiry side-by-side */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="flex items-center gap-1.5">
                                        <DollarSign className="w-3.5 h-3.5 text-emerald-500" />
                                        {t('hotel.giftAmount', 'Gift Amount ($)')}
                                    </Label>
                                    <Input
                                        type="number"
                                        value={config.gift_value || ''}
                                        onChange={e => updateConfig('gift_value', parseFloat(e.target.value) || null)}
                                        placeholder="20"
                                        min={1}
                                        className="mt-1"
                                        data-testid="hotel-gift-amount"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {t('hotel.giftAmountHint', 'Leave blank to use org default')}
                                    </p>
                                </div>
                                <div>
                                    <Label className="flex items-center gap-1.5">
                                        <Calendar className="w-3.5 h-3.5 text-blue-500" />
                                        {t('hotel.giftExpiry', 'Expiry (days)')}
                                    </Label>
                                    <Input
                                        type="number"
                                        value={config.gift_expiry_days || ''}
                                        onChange={e => updateConfig('gift_expiry_days', parseInt(e.target.value) || null)}
                                        placeholder="30"
                                        min={1} max={365}
                                        className="mt-1"
                                        data-testid="hotel-gift-expiry"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {t('hotel.giftExpiryHint', 'Leave blank to use org default')}
                                    </p>
                                </div>
                            </div>

                            {/* Voucher Validity mode */}
                            <div className="border-t pt-3 space-y-3" data-testid="hotel-validity-section">
                                <Label className="flex items-center gap-1.5">
                                    <Calendar className="w-3.5 h-3.5 text-emerald-500" />
                                    {t('hotel.voucherValidity', 'Voucher Validity')}
                                </Label>
                                <Select
                                    value={config.gift_validity_mode || 'days'}
                                    onValueChange={v => updateConfig('gift_validity_mode', v)}
                                >
                                    <SelectTrigger className="w-full" data-testid="hotel-validity-mode-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="days">{t('hotel.validityDays', 'Days after issue (default)')}</SelectItem>
                                        <SelectItem value="duration">{t('hotel.validityDuration', 'Exact duration (days + hours)')}</SelectItem>
                                        <SelectItem value="window">{t('hotel.validityWindow', 'Calendar window (start → end)')}</SelectItem>
                                    </SelectContent>
                                </Select>
                                {config.gift_validity_mode === 'duration' && (
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-xs">{t('hotel.validityDaysLabel', 'Days')}</Label>
                                            <Input type="number" min={0} max={365} value={config.gift_validity_days ?? ''}
                                                onChange={e => updateConfig('gift_validity_days', e.target.value === '' ? null : parseInt(e.target.value) || 0)}
                                                placeholder="2" className="mt-1" data-testid="hotel-validity-duration-days" />
                                        </div>
                                        <div>
                                            <Label className="text-xs">{t('hotel.validityHoursLabel', 'Hours')}</Label>
                                            <Input type="number" min={0} max={23} value={config.gift_validity_hours ?? ''}
                                                onChange={e => updateConfig('gift_validity_hours', e.target.value === '' ? null : parseInt(e.target.value) || 0)}
                                                placeholder="0" className="mt-1" data-testid="hotel-validity-duration-hours" />
                                        </div>
                                        <p className="text-xs text-muted-foreground col-span-2">
                                            {t('hotel.validityDurationHint', 'Validity starts the moment the guest claims the gift. Example: 2 days = exactly 48 hours from claim.')}
                                        </p>
                                    </div>
                                )}
                                {config.gift_validity_mode === 'window' && (
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-xs">{t('hotel.validityStart', 'Valid from')}</Label>
                                            <Input type="datetime-local"
                                                value={config.gift_window_start ? new Date(new Date(config.gift_window_start).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                                                onChange={e => updateConfig('gift_window_start', e.target.value ? new Date(e.target.value).toISOString() : null)}
                                                className="mt-1" data-testid="hotel-validity-window-start" />
                                        </div>
                                        <div>
                                            <Label className="text-xs">{t('hotel.validityEnd', 'Valid until')}</Label>
                                            <Input type="datetime-local"
                                                value={config.gift_window_end ? new Date(new Date(config.gift_window_end).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                                                onChange={e => updateConfig('gift_window_end', e.target.value ? new Date(e.target.value).toISOString() : null)}
                                                className="mt-1" data-testid="hotel-validity-window-end" />
                                        </div>
                                        <p className="text-xs text-muted-foreground col-span-2">
                                            {t('hotel.validityWindowHint', 'The voucher is redeemable only inside this exact window (your local time).')}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Language-switched gift content */}
                            <div className="border-t pt-3">
                                <LangTabs value={giftLang} onChange={setGiftLang} testIdPrefix="hotel-gift" />
                                <div className="space-y-3">
                                    <div>
                                        <Label>{t('hotel.giftTitle', 'Gift Title')} ({giftLang.toUpperCase()})</Label>
                                        <Input
                                            value={config[`gift_title_${giftLang}`] || ''}
                                            onChange={e => updateConfig(`gift_title_${giftLang}`, e.target.value)}
                                            placeholder={giftLang === 'en' ? 'Complimentary Breakfast' : giftLang === 'fr' ? 'Petit-d\u00e9jeuner offert' : 'Desayuno de cortes\u00eda'}
                                            className="mt-1"
                                            data-testid={`hotel-gift-title-${giftLang}`}
                                        />
                                    </div>
                                    <div>
                                        <Label>{t('hotel.giftDescription', 'Gift Description')} ({giftLang.toUpperCase()})</Label>
                                        <Input
                                            value={config[`gift_description_${giftLang}`] || ''}
                                            onChange={e => updateConfig(`gift_description_${giftLang}`, e.target.value)}
                                            placeholder={giftLang === 'en' ? 'Enjoy a free breakfast during your stay' : ''}
                                            className="mt-1"
                                            data-testid={`hotel-gift-desc-${giftLang}`}
                                        />
                                    </div>
                                    <div>
                                        <Label>{t('hotel.giftTerms', 'Gift Terms')} ({giftLang.toUpperCase()})</Label>
                                        <Input
                                            value={config[`gift_terms_${giftLang}`] || ''}
                                            onChange={e => updateConfig(`gift_terms_${giftLang}`, e.target.value)}
                                            placeholder={giftLang === 'en' ? 'Valid for dine-in only' : ''}
                                            className="mt-1"
                                            data-testid={`hotel-gift-terms-${giftLang}`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Rating & Timing */}
                {ratingOn && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                                <Settings2 className="w-4 h-4 text-purple-500" />
                                {t('hotel.timingConfig', 'Rating & Timing')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <Label>{t('hotel.positiveThreshold', 'Positive Rating Threshold')}</Label>
                                <Select
                                    value={String(config.positive_threshold || 4)}
                                    onValueChange={v => updateConfig('positive_threshold', parseInt(v))}
                                >
                                    <SelectTrigger className="mt-1 w-32" data-testid="hotel-threshold">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="3">3+ stars</SelectItem>
                                        <SelectItem value="4">4+ stars</SelectItem>
                                        <SelectItem value="5">5 stars only</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Review Shield Question Sets — reuses the existing org-scoped
                                QuestionManager (edit / save / apply named reusable sets). */}
                            {(() => {
                                const activeOrgId = cards.find(c => c.card_id === selectedCard)?.organization_id || null;
                                return (
                                    <div className="pt-4 mt-2 border-t space-y-3" data-testid="hotel-question-sets">
                                        <div>
                                            <Label className="text-sm font-semibold flex items-center gap-2">
                                                <Star className="w-4 h-4 text-amber-500" />
                                                {t('hotel.reviewShieldQuestionSets', 'Review Shield Question Sets')}
                                            </Label>
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                {t('hotel.reviewShieldQuestionSetsHint', 'Edit the Review Shield questions and save them as reusable, named Question Sets (e.g., Hotel, Auto Dealer, Dentist).')}
                                            </p>
                                        </div>
                                        <QuestionManager
                                            token={localStorage.getItem('yulontap_token')}
                                            organizationId={activeOrgId}
                                        />
                                    </div>
                                );
                            })()}
                        </CardContent>
                    </Card>
                )}

                {/* Messages — with language tabs (not used by Room Feedback) */}
                {tp !== 'room_feedback' && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            <Hotel className="w-4 h-4 text-amber-500" />
                            {tp === 'checkout'
                                ? t('hotel.checkoutMessages', 'Checkout Messages')
                                : t('hotel.welcomeMessages', 'Welcome Messages')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <LangTabs value={msgLang} onChange={setMsgLang} testIdPrefix="hotel-msg" />
                        {tp === 'checkout' ? (
                            <div>
                                <Label>{t('hotel.checkoutMessage', 'Checkout Message')} ({msgLang.toUpperCase()})</Label>
                                <Input
                                    value={config[`checkout_message_${msgLang}`] || ''}
                                    onChange={e => updateConfig(`checkout_message_${msgLang}`, e.target.value)}
                                    placeholder={msgLang === 'en' ? 'Thank you for staying with us' : msgLang === 'fr' ? 'Merci pour votre s\u00e9jour' : 'Gracias por hospedarse con nosotros'}
                                    className="mt-1"
                                    data-testid={`hotel-checkout-msg-${msgLang}`}
                                />
                            </div>
                        ) : (
                            <div>
                                <Label>{t('hotel.welcomeMessage', 'Welcome Message')} ({msgLang.toUpperCase()})</Label>
                                <Input
                                    value={config[`welcome_message_${msgLang}`] || ''}
                                    onChange={e => updateConfig(`welcome_message_${msgLang}`, e.target.value)}
                                    placeholder={msgLang === 'en' ? 'We hope you enjoy your stay' : msgLang === 'fr' ? 'Nous esp\u00e9rons que vous appr\u00e9cierez votre s\u00e9jour' : 'Esperamos que disfrute su estad\u00eda'}
                                    className="mt-1"
                                    data-testid={`hotel-welcome-${msgLang}`}
                                />
                            </div>
                        )}
                    </CardContent>
                </Card>
                )}
            </div>
        </div>
    );
};

export default HotelSetup;
